import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { DISPLAY_FONT, TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';
import { sectionTitle } from '../adminUi';
import TrendEmptyState from '../../components/TrendEmptyState';
import {
  coverageStatusColor,
  coverageStatusLabel,
  discoveryLaneColor,
  discoveryLaneLabel,
  discoveryStatusColor,
  discoveryStatusLabel,
  discoveryValidationColor,
  discoveryValidationLabel,
  formatShortDate,
  joinMultiValue,
  sourceGeographyLabel,
  sourceTypeLabel,
  splitMultiValue,
} from '../adminHelpers';

const ACCORDION_SX = {
  bgcolor: TREND_COLORS.cardBg,
  border: `1px solid ${TREND_COLORS.divider}`,
  borderRadius: 3,
  boxShadow: 'none',
  '&:before': { display: 'none' },
  overflow: 'hidden',
} as const;

const DISCOVERY_STATUS_ACCENT: Record<string, string> = {
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
  default: TREND_COLORS.divider,
};

export default function DiscoveryTab() {
  const {
    discoveryRun,
    busyAction,
    hasActiveDiscoveryRun,
    handleRunDiscovery,
    discoveryCandidates,
    pendingDiscoveryCount,
    importedDiscoveryCount,
    settings,
    setSettings,
    discoveryContract,
    latestDiscoveryCoverage,
    canAdmin,
    handleSaveSettings,
    activeDiscoveryQueries,
    sortedDiscoveryQueries,
    openCreateDiscoveryQueryDialog,
    openEditDiscoveryQueryDialog,
    handleDiscoveryAction,
  } = useTrendAdmin();

  const discoveryProgress = discoveryRun?.progress || null;
  const discoveryCurrent = typeof discoveryProgress?.current === 'number' ? discoveryProgress.current : null;
  const discoveryTotal = typeof discoveryProgress?.total === 'number' ? discoveryProgress.total : null;
  const hasDiscoveryProgressNumbers = typeof discoveryCurrent === 'number'
    && typeof discoveryTotal === 'number'
    && discoveryTotal > 0;
  const discoveryProgressValue = hasDiscoveryProgressNumbers
    ? Math.min(100, Math.max(0, Math.round((discoveryCurrent / discoveryTotal) * 100)))
    : undefined;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.2rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>
              Discovery utanför fasta källor
            </Typography>
            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, maxWidth: 760 }}>
              Discovery kombinerar AI-webbsök i öppna källor med riktad domäncrawl för utvalda sajter. Efter en full körning hamnar nya kandidater här för manuell granskning. Godkänn, importera eller lägg kandidater i karantän innan nästa fulla trendkörning.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={busyAction === 'discoveryRun' || busyAction === 'discoveryReview' || hasActiveDiscoveryRun}
            onClick={() => void handleRunDiscovery()}
            sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight }, flexShrink: 0 }}
          >
            {hasActiveDiscoveryRun || busyAction === 'discoveryRun' ? 'Kör discovery...' : 'Kör discovery nu'}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label={`${discoveryCandidates.length} kandidater`} variant="outlined" />
          <Chip label={`${pendingDiscoveryCount} att granska`} color={pendingDiscoveryCount > 0 ? 'info' : 'default'} />
          <Chip label={`${importedDiscoveryCount} importerade`} color={importedDiscoveryCount > 0 ? 'success' : 'default'} />
          {settings && (
            <Chip label={`Tillitspoängsgräns ${settings.sourceStrategy.discoveryMinConfidenceScore}`} variant="outlined" />
          )}
        </Stack>
        {hasActiveDiscoveryRun && discoveryProgress && (
          <Box sx={{ mt: 2.5 }}>
            <LinearProgress
              variant={hasDiscoveryProgressNumbers ? 'determinate' : 'indeterminate'}
              value={discoveryProgressValue}
              sx={{ mb: 1.5, borderRadius: 1, bgcolor: TREND_COLORS.softNavy, '& .MuiLinearProgress-bar': { bgcolor: TREND_COLORS.navy } }}
            />
            <Typography variant="body2" sx={{ color: TREND_COLORS.navy, fontWeight: 600 }}>
              Steg: {discoveryProgress.message || 'Kör discovery'}
            </Typography>
            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
              {hasDiscoveryProgressNumbers
                ? `Delmoment klara: ${discoveryCurrent}/${discoveryTotal}`
                : 'Discovery kör AI-webbsök och riktad crawl och kan ta några minuter.'}
              {typeof discoveryProgress.candidateCount === 'number' ? ` · Kandidater hittills: ${discoveryProgress.candidateCount}` : ''}
            </Typography>
          </Box>
        )}
        {discoveryRun?.status === 'failed' && discoveryRun.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {discoveryRun.error}
          </Alert>
        )}
        {discoveryRun?.status === 'completed' && discoveryRun.summary && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Senaste discovery klar: {discoveryRun.summary.candidateCount} kandidater, {discoveryRun.summary.pendingCount} redo för granskning, {discoveryRun.summary.quarantinedCount} i karantän och {discoveryRun.summary.coverage.uniqueDomains.length} unika domäner.
          </Alert>
        )}
      </Paper>

      <Accordion disableGutters defaultExpanded={false} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.05rem', fontWeight: 700, color: TREND_COLORS.navy }}>
              {discoveryContract.title}
            </Typography>
            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
              Så är discovery tänkt att fungera – spår och täckningskontrakt.
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, maxWidth: 840 }}>
            {discoveryContract.summary}
          </Typography>
          <Grid container spacing={1.5}>
            {discoveryContract.lanes.map((lane) => {
              const laneSummary = latestDiscoveryCoverage?.laneSummaries.find((entry) => entry.lane === lane.lane) || null;
              return (
                <Grid key={lane.lane} size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                      <Chip size="small" label={lane.label} color={lane.lane === 'targeted_domain_crawl' ? 'warning' : lane.lane === 'open_web_search' ? 'info' : 'default'} />
                      {laneSummary && (
                        <Chip size="small" variant="outlined" color={coverageStatusColor(laneSummary.status)} label={coverageStatusLabel(laneSummary.status)} />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                      {lane.description}
                    </Typography>
                    {laneSummary && (
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block', mt: 1 }}>
                        Konfigurerat: {laneSummary.configuredCount} · Kört: {laneSummary.executedCount} · Kandidater: {laneSummary.candidateCount} · Domäner: {laneSummary.uniqueDomainCount}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters defaultExpanded={false} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.1rem', fontWeight: 700, color: TREND_COLORS.navy }}>
              Discovery-regler
            </Typography>
            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
              Styr hur bred discovery blir: frågor, expansioner, tillit och domänspridning.
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={busyAction === 'saveSettings' || !settings || !canAdmin}
            onClick={() => void handleSaveSettings()}
            sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight }, flexShrink: 0 }}
          >
            {canAdmin ? 'Spara discovery-regler' : 'Endast admin kan spara'}
          </Button>
        </Stack>
        {settings && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Discovery-frågor per körning"
                value={settings.sourceStrategy.discoveryQueryLimit}
                onChange={(e) => setSettings((p) => p ? ({
                  ...p,
                  sourceStrategy: { ...p.sourceStrategy, discoveryQueryLimit: Number(e.target.value || 0) },
                }) : p)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Expansioner per fråga"
                value={settings.sourceStrategy.discoveryExpansionPerQuery}
                onChange={(e) => setSettings((p) => p ? ({
                  ...p,
                  sourceStrategy: { ...p.sourceStrategy, discoveryExpansionPerQuery: Number(e.target.value || 0) },
                }) : p)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min confidence för kandidat"
                value={settings.sourceStrategy.discoveryMinConfidenceScore}
                onChange={(e) => setSettings((p) => p ? ({
                  ...p,
                  sourceStrategy: { ...p.sourceStrategy, discoveryMinConfidenceScore: Number(e.target.value || 0) },
                }) : p)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Mål för domänspridning"
                value={settings.sourceStrategy.discoveryDomainDiversityTarget}
                onChange={(e) => setSettings((p) => p ? ({
                  ...p,
                  sourceStrategy: { ...p.sourceStrategy, discoveryDomainDiversityTarget: Number(e.target.value || 0) },
                }) : p)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Tillåtna språk för discovery"
                helperText="Skriv sv och/eller en med kommatecken eller radbrytningar."
                value={joinMultiValue(settings.sourceStrategy.discoveryAllowedLanguages || [])}
                onChange={(e) => setSettings((p) => p ? ({
                  ...p,
                  sourceStrategy: { ...p.sourceStrategy, discoveryAllowedLanguages: splitMultiValue(e.target.value) },
                }) : p)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                Datum- och åldersregler (<strong>Tillåt odaterat underlag</strong> och <strong>Max ålder för aktiv trend</strong>) styrs centralt under <strong>Inställningar &amp; Profil</strong>, eftersom de gäller allt källmaterial – inte bara discovery.
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                Aktiva frågor: {activeDiscoveryQueries.length}. Körningen tar de {Math.min(
                  activeDiscoveryQueries.length,
                  Math.max(1, Number(settings.sourceStrategy.discoveryQueryLimit || 0)),
                )} högst prioriterade aktiva frågorna och upp till {Math.max(
                  1,
                  Number(settings.sourceStrategy.discoveryQueryLimit || 0),
                ) * Math.max(
                  1,
                  Number(settings.sourceStrategy.discoveryExpansionPerQuery || 0),
                )} AI-webbsök-varianter.
              </Typography>
            </Grid>
            {Number(settings.sourceStrategy.discoveryQueryLimit || 0) <= 2 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  {Number(settings.sourceStrategy.discoveryQueryLimit || 0) <= 1
                    ? '1 discovery-fråga per körning ger ofta en mycket smal träffbild. Rekommenderat spann är 6-10 frågor.'
                    : '2 discovery-frågor per körning ger fortfarande en ganska smal träffbild. Rekommenderat spann är 6-10 frågor.'}
                </Alert>
              </Grid>
            )}
            {Number(settings.sourceStrategy.discoveryExpansionPerQuery || 0) <= 1 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  1 expansion per fråga ger en ganska smal AI-webbsök-bild. Rekommenderat spann är 2-4 expansioner per seed-fråga.
                </Alert>
              </Grid>
            )}
            {Number(settings.sourceStrategy.discoveryDomainDiversityTarget || 0) <= 1 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  Domänspridningsmålet är satt lågt. Höj det om du vill att discovery tydligare ska försöka hitta fler oberoende källor.
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters defaultExpanded={false} sx={ACCORDION_SX}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.1rem', fontWeight: 700, color: TREND_COLORS.navy }}>
              Täckning senaste körningen
            </Typography>
            {latestDiscoveryCoverage && (
              <Chip size="small" variant="outlined" label={`${latestDiscoveryCoverage.uniqueDomains.length} domäner`} />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, maxWidth: 820 }}>
            Vad discovery faktiskt täckte i senaste manuella discovery eller pipelinekörning med discoverysteg. Fokus ligger på coverage, inte bara antal kandidater.
          </Typography>

          {latestDiscoveryCoverage ? (
            <>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                      Körda seed-frågor
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                      {latestDiscoveryCoverage.executedQueryCount}/{latestDiscoveryCoverage.activeQueryCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                      Riktade crawl-profiler
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                      {latestDiscoveryCoverage.executedTargetedProfileCount}/{latestDiscoveryCoverage.targetedProfileCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                      Unika domäner
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                      {latestDiscoveryCoverage.uniqueDomains.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                      Frågor utan träff
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                      {latestDiscoveryCoverage.emptyQueryKeys.length}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={1.5}>
                {latestDiscoveryCoverage.laneSummaries.map((lane) => (
                  <Grid key={lane.lane} size={{ xs: 12, md: 6 }}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Chip size="small" color={coverageStatusColor(lane.status)} label={coverageStatusLabel(lane.status)} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                          {lane.label}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                        Konfigurerat {lane.configuredCount} · Kört {lane.executedCount} · Kandidater {lane.candidateCount} · Domäner {lane.uniqueDomainCount}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                      Tracks
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {latestDiscoveryCoverage.byTrack.length > 0 ? latestDiscoveryCoverage.byTrack.map((entry) => (
                        <Chip
                          key={entry.coverageKey}
                          size="small"
                          color={coverageStatusColor(entry.status)}
                          label={`${entry.label}: ${entry.candidateCount}`}
                        />
                      )) : <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>Ingen track-coverage ännu.</Typography>}
                    </Stack>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                      Kategorier
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {latestDiscoveryCoverage.byCategory.length > 0 ? latestDiscoveryCoverage.byCategory.map((entry) => (
                        <Chip
                          key={entry.coverageKey}
                          size="small"
                          color={coverageStatusColor(entry.status)}
                          label={`${entry.label}: ${entry.candidateCount}`}
                        />
                      )) : <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>Ingen kategoricoverage ännu.</Typography>}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                      Toppdomäner
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {latestDiscoveryCoverage.byDomain.length > 0 ? latestDiscoveryCoverage.byDomain.map((domainEntry) => (
                        <Chip
                          key={domainEntry.coverageKey}
                          size="small"
                          variant="outlined"
                          label={`${domainEntry.domain}: ${domainEntry.candidateCount}`}
                        />
                      )) : <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>Inga domäner loggade ännu.</Typography>}
                    </Stack>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                      Sannolikt saknad täckning
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        Hoppade över frågor p.g.a. query limit: {latestDiscoveryCoverage.skippedQueryKeys.length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        Frågor utan träff: {latestDiscoveryCoverage.emptyQueryKeys.length > 0 ? latestDiscoveryCoverage.emptyQueryKeys.join(', ') : 'Inga'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        Saknade tracks: {latestDiscoveryCoverage.missingTracks.length > 0 ? latestDiscoveryCoverage.missingTracks.join(', ') : 'Inga'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        Saknade kategorier: {latestDiscoveryCoverage.missingCategories.length > 0 ? latestDiscoveryCoverage.missingCategories.join(', ') : 'Inga'}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <Alert severity="info">
              Ingen coverage-sammanfattning finns ännu. Kör discovery eller en full trendkörning för att se vad som faktiskt täcktes.
            </Alert>
          )}
        </Stack>
        </AccordionDetails>
      </Accordion>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.1rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>
              Discovery-frågor
            </Typography>
            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, maxWidth: 760 }}>
              Redigera vilka webbfrågor discovery ska använda. Prioritet avgör ordningen, och bara aktiva frågor kan köras.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreateDiscoveryQueryDialog}
            disabled={busyAction === 'saveDiscoveryQuery'}
          >
            Ny discovery-fråga
          </Button>
        </Stack>
        {sortedDiscoveryQueries.length === 0 ? (
          <TrendEmptyState
            dense
            title="Inga discovery-frågor ännu"
            description="Lägg till en discovery-fråga för att styra vad AI-webbsöket ska leta efter."
          />
        ) : (
          <Stack spacing={1.5}>
            {sortedDiscoveryQueries.map((query) => (
              <Box
                key={query._id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: TREND_COLORS.sectionBg,
                  border: `1px solid ${TREND_COLORS.divider}`,
                  borderLeft: `3px solid ${query.active ? '#2e7d32' : TREND_COLORS.divider}`,
                  opacity: query.active ? 1 : 0.7,
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                        {query.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        {[
                          query.key,
                          query.track,
                          query.category,
                          query.language,
                          `prioritet ${query.priority}`,
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip size="small" color={query.active ? 'success' : 'default'} label={query.active ? 'Aktiv' : 'Inaktiv'} />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditDiscoveryQueryDialog(query)}
                        disabled={busyAction === 'saveDiscoveryQuery'}
                      >
                        Ändra
                      </Button>
                    </Stack>
                  </Stack>
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                    {query.prompt}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        {sectionTitle('Discovery-kö', 'Kandidater från AI-webbsök och riktad domäncrawl. Godkänn och importera bara sådant som känns fräscht, substantiellt och relevant.')}
        {discoveryCandidates.length === 0 ? (
          <TrendEmptyState
            dense
            title="Inga discovery-kandidater ännu"
            description="Kör discovery för att fylla kön med kandidater att granska och importera."
          />
        ) : (
          <Stack spacing={1.5}>
            {discoveryCandidates.map((candidate) => (
              <Box
                key={candidate._id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: TREND_COLORS.sectionBg,
                  border: `1px solid ${TREND_COLORS.divider}`,
                  borderLeft: `3px solid ${DISCOVERY_STATUS_ACCENT[discoveryStatusColor(candidate.status)] || TREND_COLORS.divider}`,
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                        {candidate.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        {[
                          candidate.expandedQueryLabel || candidate.queryLabel,
                          candidate.domain,
                          formatShortDate(candidate.publishedAt),
                          `${candidate.confidenceScore} poäng`,
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                      <Link
                        href={candidate.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="caption"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: TREND_COLORS.navy, mt: 0.25 }}
                      >
                        Öppna källa <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                      </Link>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip size="small" color={discoveryStatusColor(candidate.status)} label={discoveryStatusLabel(candidate.status)} />
                      <Chip size="small" color={discoveryLaneColor(candidate.lane)} label={discoveryLaneLabel(candidate.lane)} />
                      <Chip size="small" color={discoveryValidationColor(candidate.validationStatus)} label={discoveryValidationLabel(candidate.validationStatus)} />
                      <Chip size="small" variant="outlined" label={`Tier ${candidate.preliminarySourceTier}`} />
                      <Chip size="small" variant="outlined" label={sourceGeographyLabel(candidate.preliminaryGeography)} />
                      <Chip size="small" variant="outlined" label={candidate.category || 'mixed'} />
                      {candidate.sourceMethod && (
                        <Chip size="small" variant="outlined" label={candidate.sourceMethod === 'ai_web_search' ? 'AI-webbsök' : candidate.sourceMethod === 'category_pages' ? 'Kategorisida' : candidate.sourceMethod === 'sitemap' ? 'Sitemap' : candidate.sourceMethod} />
                      )}
                    </Stack>
                  </Stack>

                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                    {candidate.snippet || candidate.reason || 'Ingen sammanfattning tillgänglig ännu.'}
                  </Typography>
                  {candidate.coverageTags.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {candidate.coverageTags.slice(0, 4).map((tag) => (
                        <Chip key={`${candidate._id}-${tag}`} size="small" variant="outlined" label={tag.replace(/^lane:/, '').replace(/^track:/, 'spår: ').replace(/^category:/, 'kategori: ').replace(/^language:/, 'språk: ').replace(/^market:/, 'marknad: ').replace(/^crawl_mode:/, 'crawl: ').replace(/^source:/, 'källa: ')} />
                      ))}
                    </Stack>
                  )}
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={sourceTypeLabel(candidate.preliminarySourceType)} />
                        <Chip size="small" label={`Access: ${candidate.preliminaryAccessType}`} />
                        <Chip size="small" label={`Evidensvikt ${candidate.evidenceWeight}/100`} />
                      </Stack>
                      <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                        {candidate.validationReason || 'Ingen källvalidering sparad ännu.'}
                      </Typography>
                      {candidate.suggestedValidationSearches.length > 0 && (
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                          Föreslagen validering: {candidate.suggestedValidationSearches.slice(0, 3).join(' · ')}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  {candidate.reason && (
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>
                      Varför relevant: {candidate.reason}
                    </Typography>
                  )}
                  {candidate.notes && (
                    <Alert severity={candidate.status === 'quarantined' ? 'warning' : 'info'} sx={{ py: 0 }}>
                      {candidate.notes}
                    </Alert>
                  )}
                  {candidate.citations.length > 0 && (
                    <Stack spacing={0.5}>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>Källspår</Typography>
                      {candidate.citations.map((citation) => (
                        <Link
                          key={`${candidate._id}-${citation.url}-${citation.title}`}
                          href={citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          variant="caption"
                          sx={{ overflowWrap: 'anywhere' }}
                        >
                          {citation.title || citation.url}
                        </Link>
                      ))}
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      disabled={busyAction === 'discoveryReview' || candidate.status === 'approved' || candidate.status === 'imported'}
                      onClick={() => void handleDiscoveryAction(candidate._id, 'approve')}
                    >
                      Godkänn
                    </Button>
                    <Button
                      size="small"
                      disabled={busyAction === 'discoveryReview' || candidate.status === 'quarantined'}
                      onClick={() => void handleDiscoveryAction(candidate._id, 'quarantine')}
                    >
                      Karantän
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={busyAction === 'discoveryReview' || candidate.status === 'rejected'}
                      onClick={() => void handleDiscoveryAction(candidate._id, 'reject')}
                    >
                      Avvisa
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyAction === 'discoveryReview' || candidate.status !== 'approved'}
                      onClick={() => void handleDiscoveryAction(candidate._id, 'import')}
                      sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }}
                    >
                      {candidate.status === 'imported' ? 'Importerad' : 'Importera'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busyAction === 'discoveryReview'}
                      onClick={() => void handleDiscoveryAction(candidate._id, 'sourceDraft')}
                    >
                      Skapa källdraft
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
